import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DndContext, DragOverlay, MeasuringStrategy, PointerSensor, pointerWithin, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';

import WidgetFrame from './WidgetFrame';
import WidgetSlot from './WidgetSlot';
import { WIDGET_BY_ID } from './registry';
import { GRID, SIZES } from './sizes';

const noStrategy = () => null;
const SETTLE_MIN_MS = 120;
const SETTLE_BOUNCE_MS = 500;

/**
 * Desktop 12-column grid. In edit mode it is a dnd-kit sortable context whose only job is to
 * tell the store "active moved over X"; the CSS grid reflow does the rest.
 */
export default function WidgetGrid({ widgets, mode, actions, onOpenSettings, onAddWidget, scrollToId, onScrolled }) {
  const { t } = useTranslation();
  const edit = mode === 'edit';
  const [activeId, setActiveId] = useState(null);
  const [overlaySize, setOverlaySize] = useState(null);
  const snapshotRef = useRef(null);
  const containerRef = useRef(null);
  // Pending reorder, applied at most once per animation frame and never synchronously from
  // dnd-kit's own effects. Reordering reflows the CSS grid, which changes the rect under the
  // pointer, which changes `over` again: applied synchronously that chain nests until React
  // throws "Maximum update depth exceeded". The frame boundary breaks the chain; the settle
  // interval and the bounce guard (do not move onto the item we just displaced) stop the
  // visible ping-pong.
  const pendingRef = useRef({ activeId: null, overId: null, frame: null, timer: null, lastMoveAt: 0, lastOver: null });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const clearPending = useCallback(() => {
    const pending = pendingRef.current;
    if (pending.frame && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(pending.frame);
    if (pending.timer) clearTimeout(pending.timer);
    pending.frame = null;
    pending.timer = null;
    pending.overId = null;
  }, []);

  const flushPending = useCallback(() => {
    const pending = pendingRef.current;
    pending.frame = null;
    pending.timer = null;
    const { activeId: active, overId } = pending;
    if (!active || !overId || overId === active) return;
    const now = Date.now();
    if (overId === pending.lastOver && now - pending.lastMoveAt < SETTLE_BOUNCE_MS) {
      pending.overId = null;
      return;
    }
    const sinceLast = now - pending.lastMoveAt;
    if (sinceLast < SETTLE_MIN_MS) {
      pending.timer = setTimeout(flushPending, SETTLE_MIN_MS - sinceLast);
      return;
    }
    pending.overId = null;
    pending.lastMoveAt = now;
    pending.lastOver = overId;
    actions.moveById(active, overId);
  }, [actions]);

  useEffect(() => clearPending, [clearPending]);

  const onDragStart = ({ active }) => {
    snapshotRef.current = widgets.map((widget) => widget.id);
    clearPending();
    pendingRef.current.activeId = active.id;
    pendingRef.current.lastOver = null;
    pendingRef.current.lastMoveAt = 0;
    setActiveId(active.id);
    const node = containerRef.current?.querySelector(`[data-slot-id="${active.id}"]`);
    if (node) {
      const rect = node.getBoundingClientRect();
      setOverlaySize({ width: rect.width, height: rect.height });
    }
  };
  const onDragOver = ({ active, over }) => {
    const pending = pendingRef.current;
    if (!over || over.id === active.id) {
      pending.overId = null;
      return;
    }
    pending.overId = over.id;
    if (!pending.frame && !pending.timer) {
      pending.frame = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(flushPending) : setTimeout(flushPending, 16);
    }
  };
  const onDragEnd = () => {
    clearPending();
    pendingRef.current.activeId = null;
    setActiveId(null);
    setOverlaySize(null);
    snapshotRef.current = null;
  };
  const onDragCancel = () => {
    if (snapshotRef.current) actions.restoreOrder(snapshotRef.current);
    onDragEnd();
  };

  useEffect(() => {
    if (!scrollToId) return;
    const node = containerRef.current?.querySelector(`[data-slot-id="${scrollToId}"]`);
    if (node) {
      const top = node.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    onScrolled?.();
  }, [scrollToId, onScrolled]);

  const activeWidget = activeId ? widgets.find((widget) => widget.id === activeId) : null;
  const activeEntry = activeWidget ? WIDGET_BY_ID[activeWidget.id] : null;

  const grid = (
    <Box
      ref={containerRef}
      data-testid="widget-grid"
      sx={{ display: 'grid', gridTemplateColumns: `repeat(${GRID.cols}, minmax(0, 1fr))`, gridAutoRows: `${GRID.rowHeight}px`, gap: `${GRID.gap}px`, alignItems: 'stretch' }}
    >
      {widgets.map((widget) => (
        <WidgetSlot key={widget.id} widget={widget} mode={mode} isMobile={false} sortable={edit} activeId={activeId} actions={actions} onOpenSettings={onOpenSettings} />
      ))}
      {edit ? (
        <Box
          component="button"
          type="button"
          data-testid="dashboard-add-slot"
          onClick={onAddWidget}
          sx={{
            gridColumn: `span ${SIZES.S.cols}`,
            gridRow: `span ${SIZES.S.rows}`,
            border: '1.5px dashed #cbd5e1',
            borderRadius: '12px',
            bgcolor: 'transparent',
            color: '#64748b',
            fontSize: 13.5,
            fontWeight: 500,
            fontFamily: 'inherit',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <AddIcon sx={{ fontSize: 20 }} />
          {t('dashboard.edit.addWidget')}
        </Box>
      ) : null}
    </Box>
  );

  if (!edit) return grid;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <SortableContext items={widgets.map((widget) => widget.id)} strategy={noStrategy}>
        {grid}
      </SortableContext>
      {typeof document !== 'undefined'
        ? createPortal(
            <DragOverlay dropAnimation={null} zIndex={1400}>
              {activeWidget && activeEntry ? (
                <Box sx={{ width: overlaySize?.width, height: overlaySize?.height }}>
                  <WidgetFrame entry={activeEntry} id={activeWidget.id} size={activeWidget.size} opts={activeWidget.opts} mode="edit" dragging inPreview />
                </Box>
              ) : null}
            </DragOverlay>,
            document.body
          )
        : null}
    </DndContext>
  );
}
